/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { CircularProgress, Grid, Paper, Theme } from '@mui/material';
import { withStyles } from '@mui/styles';
import * as React from 'react';
import { exportTrainingAuthorizationPdf } from '../common/AuthorizationModel';
import { categories, Category } from '../common/Categories';
import { exportData, getAuthorizationDoughnutData, sendRequestAndReceiveResponse } from '../common/common';
import { fetchAuthorizationsEndpoint } from '../common/constants';
import { toAuthorizationGraphqlBody } from '../common/graphqlJsonBody';
import { loadFromLocalStorage } from '../common/localStorage';
import { history } from '../store/history';
import { dataTableRenderer, loadTableData } from './AuthorizationRenderer';
import DoughnutChart from './DoughnutChart';
import GroupedColumnChart from './GroupedColumnChart';
import LegacyGrid from './legacy/LegacyGrid';
const GridAny: any = Grid;

interface Props {
    classes?: any;
}
const styles = (theme: Theme) => ({
    table: {
        width: '100%',
        padding: '12px'
    },
    grid: {
        justifyContent: 'space-between',
        alignItems: 'center'
    }, progress: {
        margin: ((theme as any) && typeof (theme as any).spacing === 'function') ? (theme as any).spacing(2) : '16px'
    },
    progressWrap: {
        textAlign: 'center' as const,
        marginTop: '20vh'
    },
    message: {
        borderRadius: '0px',
        textAlign: 'center' as const,
        padding: '20px',
        marginTop: '20px'
    }
});

class AllTrainingAuthorizations extends React.Component<Props, {}>{
    state = {
        selectedCategory: categories.find((category: Category) => { return category.id === 11; }),
        xaxisData: [],
        dataSets: [],
        doughnutLabel: [],
        doughnutData: [],
        doughnutImageNames: [],
        rows: [],
        totalRows: 0,
        isLoading: true,
        initialLoading: true,
        isChartLoading: true,
        isModuleEnabled: false
    };
    componentDidMount() {
        console.log('AllTrainingAuthorizations - componentDidMount');
        const enabledModules: Array<any> = loadFromLocalStorage('Enabled Modules');
        let isModuleEnabled = false;
        if (this.state.selectedCategory !== undefined) {
            isModuleEnabled = enabledModules !== null ?
                enabledModules.find(x => this.state.selectedCategory !== undefined && x.modulename === this.state.selectedCategory.moduleName) !== undefined : false;
        }
        console.log('AllTrainingAuthorizations - isModuleEnabled:', isModuleEnabled, 'enabledModules:', enabledModules);
        this.setState({ isModuleEnabled });
        if (!isModuleEnabled) {
            console.warn('AllTrainingAuthorizations - Module not enabled, redirecting');
            history.push('/app/authorization');
            return;
        }
        console.log('AllTrainingAuthorizations - Loading data...');
        this.loadData();
        this.loadTableData({ pageno: 1, pagesize: 10 });
    }
    async loadData() {
        if (this.state.selectedCategory !== undefined) {
            const colChartData = await sendRequestAndReceiveResponse(this.state.selectedCategory.requestBodyforColChart, fetchAuthorizationsEndpoint);
            const xaxisData: any = [], yaxisData: any = [], datasets: any = [], doughnutLabel: any = [], doughnutData: any = [], doughnutImageNames: any = [];
            if (colChartData !== undefined && colChartData.data !== undefined && colChartData.data.companyTrainingRelation !== undefined && colChartData.data.companyTrainingRelation.data !== undefined && colChartData.data.companyTrainingRelation.data.length) {
                colChartData.data.companyTrainingRelation.data.forEach((x: any) => {
                    xaxisData.push(x.companyName);
                    yaxisData.push(x.numberOfAuthorizations);
                });
                datasets.push({
                    name: this.state.selectedCategory.yaxisLabel,
                    data: yaxisData
                });
            }
            const pieQuery = this.state.selectedCategory.requestBodyfoPieChart;
            const pieChartData = pieQuery
                ? await sendRequestAndReceiveResponse(
                    toAuthorizationGraphqlBody(pieQuery, this.state.selectedCategory.id),
                    fetchAuthorizationsEndpoint)
                : undefined;
            if (pieChartData !== undefined && pieChartData.data !== undefined && pieChartData.data.productTrainingRelation !== undefined && pieChartData.data.productTrainingRelation.data !== undefined && pieChartData.data.productTrainingRelation.data.length) {
                const result: any = getAuthorizationDoughnutData(pieChartData.data.productTrainingRelation.data);
                if (result !== undefined) {
                    result.forEach((i: any) => {
                        doughnutLabel.push(i.twodproductname);
                        doughnutData.push(i.numberOfAuthorizations);
                        doughnutImageNames.push(i.productLine);
                    });
                }
            }
            this.setState({ dataSets: datasets, xaxisData, doughnutLabel, doughnutData, doughnutImageNames, isChartLoading: false });
        }
    }

    async loadTableData(extras: any) {
        if (this.state.selectedCategory !== undefined) {
            this.setState({ isLoading: true, rows: [] });
            const data = await loadTableData(extras, this.state.selectedCategory);
            if (data !== undefined) {
                this.setState({ rows: data.rows, totalRows: data.totalRows, isLoading: false, initialLoading: false });
            }
        }
    }
    refreshChart() {

    }
    handleExport = async (exportType: any, selectedColumns: any) => {
        const mode = 'p';
        const columnHeaders: Object = exportTrainingAuthorizationPdf, fileName = 'AllTrainingAuthorizations';
        try {
            if (navigator.onLine) {
                if (this.state.selectedCategory !== undefined) {
                    const data = await loadTableData(`query{ 
	allTraining  
	{ 
		count
        data{
            companyContactTWoDProductModelId,
            firstName,
            lastName,
            email,
            companyname,
            twodProductModel,            
            certificationname,
            dateOfTraining,
            coursename,
            iscertified,
            isActive
        }  
	} 
}	`, this.state.selectedCategory, true);
                    if (data !== undefined) {
                        exportData(selectedColumns, data.rows, exportType, fileName, columnHeaders, mode);
                    }
                }

            }
        } catch (e) { }
    };
    render() {
        const { classes } = this.props;
        const { dataSets, xaxisData, doughnutLabel, doughnutData, doughnutImageNames } = this.state;
        const { initialLoading, selectedCategory, rows, totalRows, isLoading, isModuleEnabled } = this.state;


        return (
            isModuleEnabled && < Grid >
                {this.state.isChartLoading ?
                    (
                        <div className={classes.progressWrap}>
                            <CircularProgress className={classes.progress} color="secondary" />
                        </div>
                    ) :
                    this.state.dataSets !== undefined && this.state.dataSets.length > 0 ? (
                        <div>
                            <LegacyGrid container className={classes.grid}>
                                {selectedCategory !== undefined && (
                                    <LegacyGrid item xs={8}>
                                        <GroupedColumnChart
                                            datasets={dataSets}
                                            xaxisLabel={selectedCategory.xaxisLabel}
                                            xaxisData={xaxisData}
                                            yaxisLabel={selectedCategory.yaxisLabel}
                                            MaxBarNum={1}
                                            label={selectedCategory.chartLabel}
                                            category={selectedCategory}
                                        />
                                    </LegacyGrid>
                                )}
                                {selectedCategory !== undefined && doughnutLabel !== undefined && doughnutLabel.length > 0 && doughnutData !== undefined && doughnutData.length>0 && (
                                    <LegacyGrid item xs={4}>
                                        <DoughnutChart
                                            label={doughnutLabel}
                                            data={doughnutData}
                                            imageNames={doughnutImageNames}
                                            category={selectedCategory}
                                            defaultTextCategory={1}
                                            height={300}
                                        />
                                    </LegacyGrid>
                                )}
                            </LegacyGrid>
                            {dataTableRenderer(classes, initialLoading, selectedCategory, rows, totalRows, this.refreshChart.bind(this),
                                this.loadTableData.bind(this), this.handleExport.bind(this), isLoading)}
                        </div>) :
                        <Paper className={classes.message}>No data available</Paper>
                }
            </Grid>
        );
    }

}

export default withStyles(styles)(AllTrainingAuthorizations);
